import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Customer,
  CustomerLightData,
  CustomerUpdateRequest,
  DEMO_CUSTOMER,
  PageRequest,
  UpdateDataSharingData,
} from '../models';
import { AuthStore, DemoStore } from '../stores';
import { Role } from '../enums';
import { DemoDataService } from './demo-data.service';
import { CustomerCreateRequest } from '../models/customer-create-request';
import { CustomerUpdateReportEncryptionRequest } from '../models/customer-update-report-encryption.request';
import { CustomerUpdateReportEncryptionResponse } from '../models/customer-update-report-encryption.response';

@Injectable({ providedIn: 'root' })
export class CustomerService {
  private readonly httpClient = inject(HttpClient);
  private readonly authStore = inject(AuthStore);
  private readonly demoStore = inject(DemoStore);
  private readonly demoDataService = inject(DemoDataService);
  $user = this.authStore.user;

  getCustomersByRole(pageRequest: PageRequest): Observable<Customer[]> {
    if (this.demoStore.isDemoMode()) {
      if (this.$user()?.role === Role.Partner) {
        return of(this.demoDataService.getDemoCustomers());
      }
      return of([DEMO_CUSTOMER]);
    }
    switch (this.$user()?.role) {
      case Role.GlobalAdmin:
        return this.getAdminCustomers(pageRequest);
      case Role.Partner:
        return this.getPartnerCustomers(undefined, pageRequest);
      default:
        return of([]);
    }
  }

  getCustomerByRole(id: number): Observable<Customer> {
    if (this.demoStore.isDemoMode()) {
      return of(DEMO_CUSTOMER);
    }
    switch (this.$user()?.role) {
      case Role.GlobalAdmin:
        return this.getAdminCustomer(id);
      case Role.Partner:
        return this.getPartnerCustomer(id);
      default:
        return of();
    }
  }

  getCustomersLightByRole(pageRequest: PageRequest): Observable<CustomerLightData[]> {
    if (this.demoStore.isDemoMode()) {
      if (this.$user()?.role === Role.Partner) {
        return this.getPartnerCustomersLight(pageRequest);
      }
      return of(this.demoDataService.getCustomers());
    }
    switch (this.$user()?.role) {
      case Role.GlobalAdmin:
        return this.getAdminCustomersLight(pageRequest);
      case Role.Partner:
        return this.getPartnerCustomersLight(pageRequest);
      default:
        return of([]);
    }
  }

  getAdminCustomers(pageRequest: PageRequest): Observable<Customer[]> {
    return this.httpClient.get<Customer[]>('/api/admin/customers', { params: PageRequest.mapToQuery(pageRequest) });
  }

  getAdminCustomer(id: number): Observable<Customer> {
    return this.httpClient.get<Customer>(`/api/admin/customers/${id}`);
  }

  getPartnerCustomer(id: number): Observable<Customer> {
    return this.getPartnerCustomers(undefined, { page: 0, size: 1, customerId: id }).pipe(
      map((customers) => {
        if (customers.length === 0) {
          throw new Error('Customer not found');
        }
        return customers[0];
      }),
    );
  }

  getAdminCustomersLight(pageRequest: PageRequest): Observable<CustomerLightData[]> {
    return this.httpClient.get<CustomerLightData[]>('/api/admin/customers/light', {
      params: PageRequest.mapToQuery(pageRequest),
    });
  }

  getPartnerCustomers(partnerId: string | undefined, pageRequest: PageRequest): Observable<Customer[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getDemoCustomers());
    }
    return this.httpClient.get<Customer[]>(`/api/partners/${partnerId || 'me'}/customers`, {
      params: PageRequest.mapToQuery(pageRequest),
    });
  }

  getPartnerCustomersLight(pageRequest: PageRequest): Observable<CustomerLightData[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getDemoCustomers());
    }
    return this.httpClient.get<CustomerLightData[]>('/api/partners/me/customers/light', {
      params: PageRequest.mapToQuery(pageRequest),
    });
  }

  getAllPartnerCustomersLight(partnerId: string | undefined): Observable<CustomerLightData[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getDemoCustomers());
    }
    return this.httpClient.get<CustomerLightData[]>(`/api/partners/${partnerId || 'me'}/customers/all`);
  }

  getAllCustomersByRole(): Observable<CustomerLightData[]> {
    if (this.demoStore.isDemoMode()) {
      if (this.$user()?.role === Role.Partner) {
        return this.getPartnerAllCustomers();
      }
      return of(this.demoDataService.getCustomers());
    }
    switch (this.$user()?.role) {
      case Role.GlobalAdmin:
        return this.getAdminAllCustomers();
      case Role.Partner:
        return this.getPartnerAllCustomers();
      default:
        return of([]);
    }
  }

  getAdminAllCustomers(): Observable<CustomerLightData[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getCustomers());
    }
    return this.httpClient.get<CustomerLightData[]>('/api/admin/customers/all');
  }

  getPartnerAllCustomers(): Observable<CustomerLightData[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getDemoCustomers());
    }
    return this.httpClient.get<CustomerLightData[]>('/api/partners/me/customers/all');
  }

  adminCreateCustomer(customerData: CustomerCreateRequest): Observable<Customer> {
    if (this.demoStore.isDemoMode()) {
      return of(DEMO_CUSTOMER);
    }
    return this.httpClient.post<Customer>(`/api/admin/customers`, customerData);
  }

  adminUpdateCustomer(id: number, customerData: CustomerUpdateRequest): Observable<Customer> {
    if (this.demoStore.isDemoMode()) {
      return of(DEMO_CUSTOMER);
    }
    return this.httpClient.patch<Customer>(`/api/admin/customers/${id}`, customerData);
  }

  getDataSharingStatus(): Observable<boolean> {
    if (this.demoStore.isDemoMode()) {
      return of(true);
    }
    return this.httpClient
      .get<{ initialized: boolean }>('/api/customers/me/data-sharing-status')
      .pipe(map((data) => data.initialized));
  }

  updateSharingData(updateSharingData: UpdateDataSharingData): Observable<void> {
    if (this.demoStore.isDemoMode()) {
      return of();
    }
    return this.httpClient.patch<void>('/api/customers/me/share-data-with-manager', updateSharingData);
  }

  updateReportEncryptionConfig(
    config: CustomerUpdateReportEncryptionRequest,
    customerId?: number,
  ): Observable<CustomerUpdateReportEncryptionResponse> {
    if (this.demoStore.isDemoMode()) {
      return of();
    }
    return this.httpClient.put<CustomerUpdateReportEncryptionResponse>(
      `/api/customers/${customerId ?? 'me'}/configs/report-encryption`,
      config,
    );
  }

  deactivateReportEncryptionConfig(config: { mfaCode?: string }, customerId?: number): Observable<void> {
    if (this.demoStore.isDemoMode()) {
      return of();
    }
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: config,
    };
    return this.httpClient.delete<void>(`/api/customers/${customerId ?? 'me'}/configs/report-encryption`, options);
  }

  deleteCustomer(id: number): Observable<void> {
    if (this.demoStore.isDemoMode()) {
      return of();
    }
    return this.httpClient.delete<void>(`/api/admin/customers/${id}`);
  }

  getExternalNames(): Observable<string[]> {
    if (this.demoStore.isDemoMode()) {
      return of([]);
    }
    return this.httpClient.get<string[]>('/api/admin/customers/external-names');
  }
}
